import React from 'react';
import { Search, X } from 'lucide-react';
import { useAutocomplete, type ComboboxOption } from '../../hooks/useAutocomplete';

interface SearchInputProps {
    /** List of suggestions */
    options?: ComboboxOption[];
    /** Current value of the input */
    value?: string;
    /** Called when text changes */
    onChange?: (value: string) => void;
    /** Called when a suggestion is selected */
    onSelect?: (value: string) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    /** If true, the input acts like a combobox (must equal an option). Default false. */
    strict?: boolean;
}

export const SearchInput: React.FC<SearchInputProps> = ({
    options = [],
    value,
    onChange,
    onSelect,
    placeholder = 'Search...',
    className = '',
    disabled = false,
    strict = false,
}) => {
    const {
        isOpen,
        inputValue,
        setInputValue,
        highlightedIndex,
        setHighlightedIndex,
        filteredOptions,
        containerRef,
        inputRef,
        listRef,
        handleInputFocus,
        handleInputChange,
        handleOptionSelect,
        handleKeyDown,
        clearSelection,
    } = useAutocomplete({
        options,
        value: strict ? value : undefined,
        onChange: (val) => {
            if (strict) {
                onChange?.(val);
            }
        },
        onInputChange: (text) => {
            if (!strict) onChange?.(text);
        },
        onSelect: (option) => {
            onSelect?.(option.value);
            if (!strict) {
                // In non-strict mode, selecting an option updates the text 
                // and should trigger the text-change handler
                onChange?.(option.label);
            }
        },
        enforceSelection: strict,
        disabled
    });

    // Validates if the internal input value needs to be synced with the controlled prop
    React.useEffect(() => {
        if (!strict && value !== undefined && value !== inputValue) {
            setInputValue(value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, strict]);

    return (
        <div className={`combobox-container search-input-container ${className}`} ref={containerRef}>
            <div
                className={`combobox-input-wrapper ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => {
                    if (!isOpen) inputRef.current?.focus();
                }}
            >
                <div className="combobox-icon">
                    <Search size={16} />
                </div>

                <input
                    ref={inputRef}
                    type="text"
                    className="combobox-input"
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    disabled={disabled}
                />

                {inputValue && (
                    <div className="combobox-actions">
                        <div className="combobox-clear" onClick={clearSelection}>
                            <X size={14} />
                        </div>
                    </div>
                )}
            </div>

            {isOpen && !disabled && filteredOptions.length > 0 && (
                <div className="combobox-menu custom-scrollbar" ref={listRef}>
                    {filteredOptions.map((option, index) => (
                        <div
                            key={option.value}
                            className={`combobox-option ${index === highlightedIndex ? 'highlighted' : ''}`}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleOptionSelect(option);
                            }}
                            onMouseEnter={() => setHighlightedIndex(index)}
                        >
                            {option.icon && <div className="combobox-option-icon">{option.icon}</div>}
                            {option.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
